/** The options of one Custom Field permitted while its parent holds a value. */
export type CustomFieldDependencyRule = {
  parentValue: string;
  allowedOptions: readonly string[];
};

/** A Custom Field's parent field and the rules narrowing this field's options. */
export type CustomFieldDependency = {
  parentField: string;
  rules: readonly CustomFieldDependencyRule[];
};

type OptionValue = { value: string; label?: string | null };

/**
 * The options permitted while this field's parent holds `parentValue`.
 *
 * The sixth implementation of `resolve_allowed_option_values` in
 * `domain_base/entities/custom_field_dependency.py`, which the write guard uses.
 * Nothing can be shared across the language boundary or across these packages,
 * so the copies are kept in step by table tests porting that function's cases
 * verbatim - change one and the dropdown offers what the guard rejects, or hides
 * what it would accept.
 *
 *     no rules        -> unchanged, a parent link on its own permits everything
 *     matching rows   -> those options, and only those
 *     no matching row -> nothing, including when the parent has no value
 *
 * Keyed on the rules, never on a parent link existing, so a field linked to a
 * parent before any rule is written keeps all of its options rather than none.
 *
 * Always intersected with `options`, which is also what keeps the field's own
 * option order rather than the rule's.
 */
export const allowedOptions = <Option extends OptionValue>(
  rules: readonly CustomFieldDependencyRule[] | undefined,
  parentValue: unknown,
  options: Option[],
): Option[] => {
  // The same array back, not a copy: with no rules this is every render of every
  // field, and a new identity each time is needless work downstream.
  if (!rules || rules.length === 0) return options;

  const allowed = new Set(
    rules
      .filter(({ parentValue: ruleValue }) => ruleValue === parentValue)
      .flatMap(({ allowedOptions: values }) => values),
  );

  return options.filter(({ value }) => allowed.has(value));
};

/** Just enough of i18next's `t` for the two messages below. */
type Translate = (key: string, vars?: Record<string, unknown>) => string;

/**
 * The message for a stored value the rules no longer permit, or `true`.
 *
 * Narrowing removes such a value from the offered options, so the control
 * renders blank while the value itself is unchanged. Without this the form reads
 * its validity from the value, finds it non-empty, and reports valid - so the
 * learner is told by the rejected save rather than before it.
 *
 * Both cases are worded as the write guard words them, so the learner meets the
 * same words either side of the save.
 *
 * The guard lists option values; this lists their labels. They cannot disagree:
 * __unsupported_dependency_field_message (dbal_custom_field_service.py) admits
 * only a choice field whose `choices` is a string, and that branch builds each
 * option as `Option(value=option)` with no label, where `label` falls back to
 * the value. Admit the dict-backed branch there and the two start to differ.
 *
 * Call it only for a field that has a parent. Every field would otherwise report
 * invalid whenever its stored value names an option somebody deleted.
 */
export const permittedValueMessage = <Option extends OptionValue>(
  permitted: readonly Option[],
  value: unknown,
  t: Translate,
): string | true => {
  // An empty value is the required check's business, not this one's. Scalar
  // compare, as in the narrowing itself: safe only because that same guard
  // rejects multichoice on either side of a dependency, and an array value
  // would otherwise be permanently invalid, empty selection included.
  if (!value || permitted.some((option) => option.value === value)) return true;

  return permitted.length === 0
    ? t("customFieldNoValuePermitted")
    : t("customFieldValueNotAllowed", {
        choices: permitted
          .map((option) => option.label ?? option.value)
          .join(", "),
        // i18next escapes interpolated values by default and `i18n.ts` does not
        // turn that off, so an option named "Sales & Marketing" would read
        // "Sales &amp; Marketing". The help block renders text, not markup.
        interpolation: { escapeValue: false },
      });
};
